package floris.graphics.backoffice.pages.admin

import floris.graphics.core.utils.TokenManager
import floris.graphics.repo.AuthRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val AUTO_HIDE_TIME = 3000L

class LoginViewModel(
    private val authRepo: AuthRepo = AuthRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {

    private val _state = MutableStateFlow(LoginState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<LoginUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    init {
        coroutineScope.launch {
            state.collectLatest { currentState ->
                if (currentState.errorText.isNotBlank()) {
                    delay(AUTO_HIDE_TIME) // Delay before auto-resetting error
                    _state.update {
                        // Only clear if error hasn't changed
                        if (it.errorText == currentState.errorText) {
                            it.copy(errorText = "")
                        } else it
                    }
                }
            }
        }
    }

    fun onEvent(event: LoginEvent) = coroutineScope.launch {
        when (event) {
            is LoginEvent.Login -> login(_state.value.emailAddress, _state.value.password, _state.value.remember)
            is LoginEvent.UpdateEmail -> _state.update { it.copy(emailAddress = event.value) }
            is LoginEvent.UpdatePassword -> _state.update { it.copy(password = event.value) }
            is LoginEvent.UpdateRemember -> _state.update { it.copy(remember = event.remember) }
        }
    }

    @Suppress("TooGenericExceptionCaught")
    private suspend fun login(email: String, password: String, remember: Boolean) {
        if (email.isNotEmpty() && password.isNotEmpty()) {
            try {
                val tokens = authRepo.login(email, password)
                TokenManager.set(tokens, remember = remember)
                _eventFlow.send(LoginUiEvent.NavigateToNextScreen)
            } catch (e: Exception) {
                _state.update { it.copy(errorText = e.message ?: "") }
            }
        } else {
            _state.update { it.copy(errorText = "Input fields are empty") }
        }
    }
}

data class LoginState(
    val emailAddress: String = "",
    val password: String = "",
    val remember: Boolean = false,
    val errorText: String = ""
) {
    fun isValid(): Boolean {
        return emailAddress.isNotEmpty() && password.isNotEmpty()
    }
}

sealed class LoginEvent {
    data object Login : LoginEvent()
    data class UpdateEmail(val value: String) : LoginEvent()
    data class UpdatePassword(val value: String) : LoginEvent()
    data class UpdateRemember(val remember: Boolean) : LoginEvent()
}

sealed class LoginUiEvent {
    data object NavigateToNextScreen : LoginUiEvent()
}
